// OVA contrast guard: keeps every text readable whatever the background behind it.
// Product pages and the cart drawer change colour with the selected variant and with the
// items in the cart, and future products bring palettes nobody has checked. Instead of
// listing colours per product, this measures what is actually painted: for each visible
// text it resolves the real background (solid colours, translucent layers and gradients),
// computes the WCAG contrast ratio and, below 4.5:1 (3:1 for large text), switches the
// text to white or ink — whichever reads best — with a soft shadow on busy gradients.
// The theme's own colours are restored as soon as they become readable again.

const LIGHT = [255, 255, 255];
const DARK = [13, 11, 38];
const SKIP = 'script, style, noscript, template, svg, canvas, video, iframe, [aria-hidden="true"], .visually-hidden, ova-cosmos, slideshow-controls, .slideshow-control, [class*="star-rating"], .jdgm-visually-hidden, [data-ova-contrast="off"]';
// Where colours move with products and cart contents. Text over photos and videos (heroes,
// media overlays) is left alone: its real background cannot be measured from CSS.
const SCOPES = [
  '#MainContent .product-information', '#MainContent product-recommendations', '.ova-cosmos-host', '.shopify-section:has(> ova-cosmos)',
  '#cart-drawer', 'cart-drawer-component', '.cart-items', '.cart-summary', '.cart__ctas', '.cart-totals',
  'quick-add-dialog', '.sticky-add-to-cart__bar', '.jdgm-widget', '[data-ova-contrast="on"]',
].join(', ');
const fixed = new Map(); // element -> original inline color / text-shadow

// Computed colours come as rgb()/rgba(), or as color(srgb r g b / a) when built with color-mix().
const COLOR = /rgba?\([^)]+\)|color\(srgb[^)]+\)/g;
const parse = (value) => {
  const m = value && value.match(/(rgba?|color)\(([^)]+)\)/);
  if (!m) return null;
  const p = m[2].replace('srgb', '').split(/[\s,/]+/).filter(Boolean).map((v) => (v.endsWith('%') ? parseFloat(v) / 100 : parseFloat(v)));
  if (m[1] === 'color') return [p[0] * 255, p[1] * 255, p[2] * 255, p[3] ?? 1];
  return [p[0], p[1], p[2], p[3] ?? 1];
};
const channel = (v) => {
  v /= 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};
const luminance = ([r, g, b]) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
const ratio = (a, b) => {
  const [x, y] = [luminance(a), luminance(b)].sort((m, n) => n - m);
  return (x + 0.05) / (y + 0.05);
};
const over = (top, base) => base.map((v, i) => v * (1 - top[3]) + top[i] * top[3]);

// Effective background of an element: composites translucent layers down to the first
// opaque one. Gradients count as the average of their stops, flagged as "busy" when the
// stops are far apart. Photos make the background unknowable: returns null.
function background(el, cache) {
  const layers = [];
  let busy = false;
  for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
    if (cache.has(node)) {
      const hit = cache.get(node);
      if (!hit) return null;
      let base = hit.color;
      for (let i = layers.length - 1; i >= 0; i--) base = over(layers[i], base);
      return { color: base, busy: busy || hit.busy };
    }
    const cs = getComputedStyle(node);
    const image = cs.backgroundImage;
    if (image && image !== 'none') {
      if (image.includes('url(')) return null;
      const stops = [...image.matchAll(COLOR)].map((m) => parse(m[0])).filter((c) => c && c[3] > 0.25);
      if (stops.length) {
        const avg = [0, 1, 2].map((i) => stops.reduce((s, c) => s + c[i], 0) / stops.length);
        const lums = stops.map(luminance);
        busy = busy || Math.max(...lums) - Math.min(...lums) > 0.35;
        const alpha = Math.min(1, stops.reduce((s, c) => s + c[3], 0) / stops.length);
        layers.push([...avg, alpha]);
        if (alpha >= 0.95) break;
        continue;
      }
    }
    const c = parse(cs.backgroundColor);
    if (c && c[3] > 0) {
      layers.push(c);
      if (c[3] >= 0.99) break;
    }
  }
  let base = [255, 255, 255];
  for (let i = layers.length - 1; i >= 0; i--) base = over(layers[i], base);
  const result = { color: base, busy };
  cache.set(el, result);
  return result;
}


function restore(el) {
  const original = fixed.get(el);
  if (!original) return;
  el.style.setProperty('color', original.color, original.colorPriority);
  el.style.setProperty('text-shadow', original.shadow, original.shadowPriority);
  if (!el.getAttribute('style')) el.removeAttribute('style');
  delete el.dataset.ovaContrast;
  fixed.delete(el);
}

function check(el, cache) {
  // Measure with transitions frozen: otherwise lifting a previous fix starts a colour
  // transition and the reading is an in-between colour, not the theme's real one.
  const transition = el.style.getPropertyValue('transition');
  const transitionPriority = el.style.getPropertyPriority('transition');
  el.style.setProperty('transition', 'none', 'important');
  try {
    measure(el, cache);
  } finally {
    getComputedStyle(el).color; // apply the outcome before transitions come back
    el.style.setProperty('transition', transition, transitionPriority);
    if (!el.getAttribute('style')) el.removeAttribute('style');
  }
}

function measure(el, cache) {
  // Judge the theme's natural colour, not a previous fix.
  restore(el);
  const rect = el.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return;
  const cs = getComputedStyle(el);
  if (cs.visibility !== 'visible' || cs.webkitTextFillColor === 'rgba(0, 0, 0, 0)' || cs.webkitTextFillColor === 'transparent') return;
  const fg = parse(cs.color);
  if (!fg || fg[3] === 0) return;
  // Intentionally dimmed UI (sold-out variants…) stays as designed. Ancestor opacity is ignored:
  // scroll reveals fade whole blocks in, and their text must be right once fully shown.
  if (parseFloat(cs.opacity) < 0.75 || el.closest('[aria-disabled="true"], :disabled')) return;
  const bg = background(el, cache);
  if (!bg) return;
  // The element's own opacity dims its text against the background behind it.
  const painted = over([fg[0], fg[1], fg[2], fg[3] * parseFloat(cs.opacity)], bg.color);
  const size = parseFloat(cs.fontSize);
  const large = size >= 24 || (size >= 18.66 && parseInt(cs.fontWeight, 10) >= 700);
  if (ratio(painted, bg.color) >= (large ? 3 : 4.5)) return;
  const pick = ratio(LIGHT, bg.color) >= ratio(DARK, bg.color) ? LIGHT : DARK;
  fixed.set(el, {
    color: el.style.getPropertyValue('color'), colorPriority: el.style.getPropertyPriority('color'),
    shadow: el.style.getPropertyValue('text-shadow'), shadowPriority: el.style.getPropertyPriority('text-shadow'),
  });
  el.dataset.ovaContrast = pick === LIGHT ? 'light' : 'dark';
  el.style.setProperty('color', `rgb(${pick.join(' ')})`, 'important');
  if (bg.busy) el.style.setProperty('text-shadow', pick === LIGHT ? '0 1px 3px rgb(0 0 0 / .55)' : '0 1px 2px rgb(255 255 255 / .6)', 'important');
}

// Elements that directly hold visible text.
function textElements(root) {
  const out = new Set();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => (node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
  });
  while (walker.nextNode()) {
    const el = walker.currentNode.parentElement;
    if (el && !el.closest(SKIP) && el.closest(SCOPES)) out.add(el);
  }
  return [...out];
}

let queued = 0;
let running = false;
function run() {
  queued = 0;
  if (running) return schedule(120);
  running = true;
  // Fixed elements that left the page are forgotten.
  for (const el of fixed.keys()) if (!el.isConnected) fixed.delete(el);
  const queue = textElements(document.body);
  const cache = new WeakMap();
  // Time-sliced so long pages never block input.
  const step = (deadline) => {
    const until = performance.now() + Math.min(10, deadline?.timeRemaining?.() || 10);
    while (queue.length && performance.now() < until) check(queue.pop(), cache);
    if (queue.length) idle(step);
    else running = false;
  };
  step();
}
const idle = (fn) => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 200 }) : setTimeout(fn, 16));
// Debounced, but never starved: pages that mutate constantly (animations, review widgets)
// still get a pass at least every 600 ms.
let firstRequest = 0;
function schedule(delay = 180) {
  const now = performance.now();
  if (!queued) firstRequest = now;
  clearTimeout(queued);
  queued = setTimeout(run, Math.max(0, Math.min(delay, firstRequest + 600 - now)));
}

// Re-check when the page changes: variant palettes (they animate for ~450 ms), cart updates,
// drawers opening, sections re-rendered by Horizon, theme editor changes.
// Class changes only matter on page-level containers; decorative loops toggle classes constantly.
const WATCHED_CLASS = 'html, body, dialog, details, cart-drawer-component, product-recommendations, .product-information';
new MutationObserver((records) => {
  for (const r of records) {
    const target = r.target instanceof Element ? r.target : r.target.parentElement;
    if (!target || target.closest('ova-cosmos')) continue;
    if (r.type === 'childList' && !target.closest(SCOPES) && !target.querySelector?.(SCOPES)) continue;
    if (r.type === 'attributes' && r.attributeName === 'class' && !target.matches(WATCHED_CLASS)) continue;
    return schedule();
  }
}).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'class', 'data-ova-product', 'hidden'] });
for (const type of ['variant:update', 'cart:update', 'product:select', 'shopify:section:load']) document.addEventListener(type, () => schedule(520));
document.documentElement.addEventListener('transitionend', (e) => {
  if (e.target === document.documentElement) schedule(60);
});
// Content revealed on scroll or opened accordions: check once the motion settles.
for (const type of ['animationend', 'transitionend']) {
  document.addEventListener(type, (e) => {
    if (e.target instanceof Element && e.target.closest(SCOPES) && !e.target.closest('ova-cosmos')) schedule(120);
  }, true);
}
window.addEventListener('pageshow', () => schedule(60));
schedule(document.readyState === 'complete' ? 60 : 400);

window.ovaContrast = { run: () => schedule(0) };
